import {OnDestroy, OnInit} from "@angular/core";
import {WorkflowNavigationDirection, WorkflowNavigationInfo, WorkflowService} from "./workflow.service";
import {ActivatedRoute, Router, UrlSegment} from "@angular/router";
import {Subscription} from "rxjs";
import {WorkflowTargetInfoModel} from "./models/workflow-target-info.model";
import {WorkflowStepStatus} from "./models/workflow-step-status.enum";
import {exposePropertyInDebugMode} from "../misc/expose-item-in-debug-mode.function";
import {debounceTime} from "rxjs/operators";

export abstract class WorkflowStep implements OnInit, OnDestroy {
  /* put these in your component's constructor as public variables */
  abstract route: ActivatedRoute;
  abstract router: Router;
  abstract workflowService: WorkflowService;

  hideTaskOnInit: boolean; // can be set via super() - default false
  workflowTargetInfo: WorkflowTargetInfoModel = {stepRoles: []} as WorkflowTargetInfoModel; // set right before ready()
  dirty: boolean; // adjust this value to trigger save on navigation (or implement checkDirty())
  saveOnContinue: boolean; // adjust this value to trigger save on all forward navigation (no prompt)

  private _workflowStatusSubscription: Subscription;
  private _workflowNavigationSubscription: Subscription;
  private _routeSubscription: Subscription;

  /**
   * Called when the workflow is ready for the step to load data.
   * WARNING: This can be called multiple times in the step's lifecycle!
   * The step should assume "something" changed when this is fired.
   * @param {WorkflowTargetInfoModel} [workflowTargetInfo]
   * @param {any} [extraData]
   */
  abstract ready(workflowTargetInfo?: WorkflowTargetInfoModel, extraData?: any): any;

  get loading(): boolean {
    if (!this.workflowService) return false;

    return this.workflowService.taskLoading;
  }

  set loading(loading: boolean) {
    if (!this.workflowService) {
      console.log(`Tried to set loading=${loading} but workflowService isn't here yet!`);
      return;
    }

    this.workflowService.taskLoading = loading;
  }

  get isLocked(): boolean {
    return (!!this.workflowService.workflowConfig.finished || this.workflowService.userIsReadOnly);
  }

  /**
   * Override to provide a function-based dirty check.  Good if you have to check a NgForm or something.
   */
  checkDirty(): boolean {
    return false;
  }

  /**
   * Override this method to handle save and dirty-check logic.
   * Resolve with "true" to permit navigation.  Any other resolution or rejection will prevent navigation.
   * @returns {Promise<boolean>}
   */
  autoSave(): Promise<boolean | string> {
    return Promise.resolve(true);
  }

  /**
   * Call from your component to continue to the next step (if available)
   */
  autoNextStep(): void {
    this.workflowService.navigate(undefined, WorkflowNavigationDirection.Next, this.workflowService.nextStep);
  }

  private notifyThenContinue({direction, targetStep}: WorkflowNavigationInfo): Promise<boolean> {
    if (direction !== WorkflowNavigationDirection.Next || !targetStep.hasNewRoles) {
      document.location.href = targetStep.url;
      return Promise.resolve(true);
    }

    return this.workflowService.showNotificationDialog(targetStep)
      .then(() => {
        document.location.href = targetStep.url;
        return true;
      });
  }

  private async autoSaveWrapper({direction, targetStep}: WorkflowNavigationInfo): Promise<boolean> {
    const r = await this.autoSave();

    if (typeof r === "boolean") {
      if (r) {
        await this.workflowService.setStepStatus(WorkflowStepStatus.Completed);

        return this.notifyThenContinue({direction, targetStep});
      }

      return false;
    }

    if (typeof r === "string") {
      await this.workflowService.setStepStatus(WorkflowStepStatus.Completed);

      window.location.href = `#/${r}`;
    }

    return false;
  }

  private async updateWorkflowTargetInfo(workflowTargetInfo: WorkflowTargetInfoModel): Promise<void> {
    if (!workflowTargetInfo) return;

    this.workflowTargetInfo = workflowTargetInfo;
    this.ready(workflowTargetInfo, this.workflowService.getExtraData());

    exposePropertyInDebugMode(
      "currentWorkflowStep",
      {
        configurable: true,
        enumerable: true,
        get: () => this,
      },
    );

    exposePropertyInDebugMode(
      "currentWorkflowStepDirty",
      {
        configurable: true,
        enumerable: true,
        get: (): boolean => this.dirty,
        set: (dirty: boolean) => { this.dirty = dirty; },
      },
    );

    exposePropertyInDebugMode(
      "workflowTargetInfo",
      {
        configurable: true,
        enumerable: true,
        get: () => this.workflowTargetInfo,
      },
    );
  }

  private async handleWorkflowNavigation({direction, targetStep}: WorkflowNavigationInfo) {
    if (this.workflowTargetInfo.blocked || !this.workflowTargetInfo.correctRole || this.isLocked) {
      await this.notifyThenContinue({direction, targetStep});
      return;
    }

    if (this.saveOnContinue && direction === WorkflowNavigationDirection.Next) {
      await this.autoSaveWrapper({direction, targetStep})
        .catch((error) => { console.error(error); });
      return;
    }

    if (this.dirty === false && this.checkDirty() === false) {
      if (
        direction === WorkflowNavigationDirection.Next &&
        this.workflowService.currentStep &&
        this.workflowService.currentStep.status !== WorkflowStepStatus.Completed
      ) {
        await this.workflowService.setStepStatus(WorkflowStepStatus.Completed);
      }

      await this.notifyThenContinue({direction, targetStep});
      return;
    }

    if (direction === WorkflowNavigationDirection.Next) {
      await this.autoSaveWrapper({direction, targetStep})
        .catch((error) => { console.error(error); });
      return;
    }

    const result = await this.workflowService.showUnsavedChangesWarning();

    switch (result) {
      case "save":
        await this.autoSaveWrapper({direction, targetStep})
          .catch((error) => { console.error(error); });
        break;
      case "continue":
        await this.notifyThenContinue({direction, targetStep});
        break;
      case "cancel":
      default:
        break;
    }
  }

  ngOnInit(): void {
    if (this.hideTaskOnInit) {
      this.loading = true;
    }

    this._workflowStatusSubscription = this.workflowService.workflowStatus
      .pipe(debounceTime(100)) // let router figure itself out
      .subscribe(
        result => this.updateWorkflowTargetInfo(result)
          .catch((error) => { console.error(error); }),
      );

    this._workflowNavigationSubscription = this.workflowService.workflowNavigation
      .subscribe(
        result => this.handleWorkflowNavigation(result)
          .catch((error) => { console.error(error); }),
      );

    this._routeSubscription = this.route.url
      .subscribe(
        (value: UrlSegment[] = []) => {
          const paths = value.map(it => it.path);
          this.workflowService.updateWorkflowStep(paths[0], paths[1] || "");
        },
      );
  }

  ngOnDestroy(): void {
    if (this._workflowStatusSubscription) this._workflowStatusSubscription.unsubscribe();
    if (this._workflowNavigationSubscription) this._workflowNavigationSubscription.unsubscribe();
    if (this._routeSubscription) this._routeSubscription.unsubscribe();
  }

  /**
   * By default, the task will be shown on init.
   * You will need to set loading = false when data is loaded to counter te effects of this optional parameter.
   * @param hideTaskOnInit should be set to true to hide task when the task loads.
   */
  constructor(
    hideTaskOnInit: boolean = false,
  ) {
    this.dirty = false;
    this.hideTaskOnInit = hideTaskOnInit;
  }
}
